<template>
  <div class="ai-distillation-container">
    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 顶部按钮组 -->
      <div class="button-group">
        <button v-for="button in mainButtons" :key="button.id" class="main-button"
          :class="{ active: activeMainButton === button.id }" @click="handleMainButtonClick(button.id)">
          {{ button.label }}
        </button>
      </div>

      <!-- 内容区域 -->
      <div class="content-area">
        <!-- 蒸馏操作区域 -->
        <div class="distillation-area">
          <div class="search-section">
            <div class="search-input-group">
              <el-input v-model="searchKeyword" :placeholder="getPlaceholder()" class="keyword-input" clearable>
                <template #prefix>
                  <el-icon>
                    <Search />
                  </el-icon>
                </template>
              </el-input>

              <!-- 行业选择框（搜索词和问答词显示） -->
              <el-select v-model="selectedIndustry" placeholder="请选择行业" class="industry-select"
                v-if="activeMainButton === 'search' || activeMainButton === 'qa'">
                <el-option label="制造业" value="manufacturing" />
                <el-option label="服务业" value="service" />
              </el-select>

              <el-button type="primary" class="distill-button" @click="handleDistill" :loading="distilling">
                <el-icon>
                  <Filter />
                </el-icon>
                蒸馏
              </el-button>
            </div>
          </div>

          <!-- 蒸馏结果展示 -->
          <div class="result-section" v-if="showResults">
            <div class="result-header">
              <h3 class="result-title">蒸馏结果</h3>
              <div class="result-stats">
                <span class="stat-item">关键词: {{ resultStats.keywordCount }} 个</span>
                <span class="stat-item">提炼时间: {{ resultStats.processTime }} 秒</span>
                <span class="stat-item">优化率: {{ resultStats.optimizationRate }}%</span>
                <span class="stat-item">消耗点数: {{ resultStats.pointsUsed }}</span>
                <span class="stat-item">剩余点数: {{ resultStats.remainingPoints }}</span>
              </div>

              <!-- 一键复制按钮 -->
              <div class="batch-actions" v-if="hasResults">
                <el-button size="small" type="success" @click="handleBatchCopy">
                  <el-icon>
                    <DocumentCopy />
                  </el-icon>
                  一键复制全部
                </el-button>
              </div>
            </div>

            <div class="result-content">
              <!-- 搜索词结果 -->
              <div v-if="activeMainButton === 'search'" class="keywords-grid">
                <div v-for="(keyword, index) in searchKeywords" :key="index" class="keyword-card">
                  <div class="card-header">
                    <div class="keyword-text">{{ keyword.text }}</div>
                    <el-button class="copy-icon" size="small" text @click="handleCopy(keyword.text)">
                      <el-icon>
                        <CopyDocument />
                      </el-icon>
                    </el-button>
                  </div>
                  <div class="keyword-meta">
                    <span class="meta-item">热度: {{ keyword.heat }}</span>
                    <span class="meta-item">竞争: {{ keyword.competition }}</span>
                  </div>
                </div>
              </div>

              <!-- 问答词结果 -->
              <div v-if="activeMainButton === 'qa'" class="qa-list">
                <div v-for="(qa, index) in qaResults" :key="index" class="qa-card">
                  <div class="card-header">
                    <div class="qa-question">
                      <el-icon class="qa-icon">
                        <QuestionFilled />
                      </el-icon>
                      {{ qa.question }}
                    </div>
                    <el-button class="copy-icon" size="small" text @click="handleCopy(qa.question)">
                      <el-icon>
                        <CopyDocument />
                      </el-icon>
                    </el-button>
                  </div>
                  <div class="qa-answer">
                    <el-icon class="qa-icon">
                      <ChatDotRound />
                    </el-icon>
                    {{ qa.answer }}
                  </div>
                  <div class="qa-actions">
                    <el-button size="small" text @click="handleCopy(qa.answer)">
                      <el-icon>
                        <CopyDocument />
                      </el-icon>
                      复制答案
                    </el-button>
                  </div>
                </div>
              </div>

              <!-- 品牌词结果 -->
              <div v-if="activeMainButton === 'brand'" class="brand-list">
                <div v-for="(brand, index) in brandResults" :key="index" class="brand-card">
                  <div class="card-header">
                    <div class="brand-header">
                      <div class="brand-name">{{ brand.name }}</div>
                      <el-tag :type="brand.type === '核心' ? 'danger' : 'warning'" size="small">
                        {{ brand.type }}
                      </el-tag>
                    </div>
                    <el-button class="copy-icon" size="small" text @click="handleCopy(brand.name)">
                      <el-icon>
                        <CopyDocument />
                      </el-icon>
                    </el-button>
                  </div>
                  <div class="brand-desc">{{ brand.description }}</div>
                  <div class="brand-stats">
                    <span class="stat">搜索量: {{ brand.searchVolume }}</span>
                    <span class="stat">相关词: {{ brand.relatedCount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 历史记录 -->
          <div class="history-section" v-if="historyRecords.length > 0">
            <div class="history-header">
              <h3 class="history-title">历史蒸馏记录</h3>
              <el-button size="small" @click="clearHistory">清空历史</el-button>
            </div>
            <div class="history-list">
              <div v-for="record in historyRecords" :key="record.id" class="history-item">
                <div class="history-main" @click="toggleHistoryDetail(record.id)">
                  <div class="history-content">
                    <span class="history-keyword">{{ record.keyword }}</span>
                    <!-- 修改这一行 -->
                    <span class="history-user">
                      <el-icon class="user-icon">
                        <User />
                      </el-icon>
                      {{ record.user_nickname }}
                    </span>
                    <span class="history-type">{{ getTypeLabel(record.type) }}</span>
                    <span class="history-count">{{ record.resultCount }}个结果</span>
                    <span class="history-points">-{{ record.pointsDeducted }}点</span>
                    <span class="history-time">{{ record.time }}</span>
                  </div>
                  <div class="history-actions">
                    <el-icon class="expand-icon" :class="{ rotated: expandedHistory.includes(record.id) }">
                      <ArrowDown />
                    </el-icon>
                  </div>
                </div>

                <!-- 历史记录详情 -->
                <div class="history-detail" v-if="expandedHistory.includes(record.id)">
                  <div class="detail-content">
                    <!-- 搜索词历史详情 -->
                    <div v-if="record.type === 'search'" class="keywords-grid detail-grid">
                      <div v-for="(keyword, index) in record.distilled_keywords" :key="index"
                        class="keyword-card detail-card">
                        <div class="card-header">
                          <div class="keyword-text">{{ keyword }}</div>
                          <el-button class="copy-icon" size="small" text @click="handleCopy(keyword)">
                            <el-icon>
                              <CopyDocument />
                            </el-icon>
                          </el-button>
                        </div>
                      </div>
                    </div>

                    <!-- 问答词历史详情 -->
                    <div v-if="record.type === 'qa'" class="qa-list detail-list">
                      <div v-for="(qa, index) in record.distilled_keywords" :key="index" class="qa-card detail-card">
                        <div class="card-header">
                          <div class="qa-question">
                            <el-icon class="qa-icon">
                              <QuestionFilled />
                            </el-icon>
                            {{ qa.question }}
                          </div>
                          <el-button class="copy-icon" size="small" text @click="handleCopy(qa.question)">
                            <el-icon>
                              <CopyDocument />
                            </el-icon>
                          </el-button>
                        </div>
                        <div class="qa-answer">
                          <el-icon class="qa-icon">
                            <ChatDotRound />
                          </el-icon>
                          {{ qa.answer }}
                        </div>
                        <div class="qa-actions">
                          <el-button size="small" text @click="handleCopy(qa.answer)">
                            <el-icon>
                              <CopyDocument />
                            </el-icon>
                            复制答案
                          </el-button>
                        </div>
                      </div>
                    </div>

                    <!-- 品牌词历史详情 -->
                    <div v-if="record.type === 'brand'" class="brand-list detail-list">
                      <div v-for="(brand, index) in record.distilled_keywords" :key="index"
                        class="brand-card detail-card">
                        <div class="card-header">
                          <div class="brand-header">
                            <div class="brand-name">{{ brand.name }}</div>
                            <el-tag :type="brand.type === '核心' ? 'danger' : 'warning'" size="small">
                              {{ brand.type }}
                            </el-tag>
                          </div>
                          <el-button class="copy-icon" size="small" text @click="handleCopy(brand.name)">
                            <el-icon>
                              <CopyDocument />
                            </el-icon>
                          </el-button>
                        </div>
                        <div class="brand-desc">{{ brand.description }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="detail-actions">
                    <el-button size="small" @click="handleRedistill(record)">重新蒸馏</el-button>
                    <el-button size="small" type="primary" @click="handleBatchCopyHistory(record)">
                      <el-icon>
                        <DocumentCopy />
                      </el-icon>
                      复制全部结果
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Search,
  Filter,
  QuestionFilled,
  ChatDotRound,
  CopyDocument,
  DocumentCopy,
  ArrowDown
} from '@element-plus/icons-vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
  name: 'AiDistillation',
  components: {
    Search,
    Filter,
    QuestionFilled,
    ChatDotRound,
    CopyDocument,
    DocumentCopy,
    ArrowDown
  },
  data() {
    return {
      userList: [], // 用户列表
      userMap: {},  // 用户ID映射
      userLoading: false,
      // 响应式数据
      activeMainButton: 'search',
      searchKeyword: '',
      selectedIndustry: 'manufacturing',
      distilling: false,
      showResults: false,
      expandedHistory: [], // 展开的历史记录ID

      // 主按钮配置
      mainButtons: [
        { id: 'search', label: '搜索词' },
        { id: 'qa', label: '问答词' },
        { id: 'brand', label: '品牌词' }
      ],

      // 蒸馏结果统计
      resultStats: {
        keywordCount: 0,
        processTime: 0,
        optimizationRate: 0,
        pointsUsed: 0,
        remainingPoints: 0
      },

      // 蒸馏结果数据
      searchKeywords: [],
      qaResults: [],
      brandResults: [],

      // 历史记录
      historyRecords: []
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    getPlaceholder() {
      return () => {
        const placeholders = {
          search: '请输入搜索关键词',
          qa: '请输入问题关键词',
          brand: '请输入品牌关键词'
        }
        return placeholders[this.activeMainButton] || '请输入关键词'
      }
    },
    hasResults() {
      switch (this.activeMainButton) {
        case 'search':
          return this.searchKeywords.length > 0
        case 'qa':
          return this.qaResults.length > 0
        case 'brand':
          return this.brandResults.length > 0
        default:
          return false
      }
    }
  },
  mounted() {
    this.fetchHistory();
    this.fetchUserList();
  },
  methods: {
    handleMainButtonClick(buttonId) {
      this.activeMainButton = buttonId
      this.showResults = false
      this.searchKeyword = ''
    },


    // 获取用户列表
    async fetchUserList() {
      this.userLoading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/site/api/associated_user.php`,
          {
            action: 'get_users',
            store_id: this.userInfo.id
          }
        )

        if (response.data.data) {
          this.userList = response.data.data
          // 构建用户映射
          this.userMap = {}
          response.data.data.forEach(user => {
            this.userMap[user.id] = user
          })
        }
      } catch (error) {
        console.error('获取用户列表错误:', error)
      } finally {
        this.userLoading = false
      }
    },

    // 专门用于获取用户昵称的方法
    getUserNickname(userId) {
      // 如果 user_id 为 null 或 undefined，返回当前用户昵称
      if (!userId) {
        return this.userInfo.nickname || '系统用户'
      }

      // 从用户映射中查找
      const user = this.userMap[userId]
      return user?.nickname || '未知用户'
    },

    // 原有的 getUserInfo 方法可以保留用于其他需要完整用户信息的场景
    getUserInfo(userId) {
      if (!userId) {
        return {
          nickname: this.userInfo.nickname || '系统用户',
          phone: this.userInfo.phone || '',
          avatar: this.userInfo.avatar || ''
        }
      }

      const user = this.userMap[userId]
      if (user) {
        return {
          nickname: user.nickname,
          phone: user.phone,
          avatar: user.avatar
        }
      }

      return {
        nickname: '未知用户',
        phone: '',
        avatar: ''
      }
    },
    async handleDistill() {
      if (!this.searchKeyword.trim()) {
        ElMessage.warning('请输入关键词')
        return
      }

      this.distilling = true
      this.showResults = false

      try {
        const params = {
          action: 'distill',
          store_id: this.userInfo.id,
          keyword: this.searchKeyword.trim(),
          distillation_type: this.activeMainButton,
          industry: this.selectedIndustry
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/site/api/distillation_management.php`,
          params
        )

        if (data.success) {
          // 更新结果数据
          this.updateResultsData(data.data)

          // 更新统计信息
          this.resultStats = {
            keywordCount: data.stats.keywordCount,
            processTime: data.stats.processTime,
            optimizationRate: data.stats.optimizationRate,
            pointsUsed: this.formatPoints(data.stats.pointsUsed), // 格式化点数
            remainingPoints: this.formatPoints(data.stats.remainingPoints) // 格式化点数
          }

          this.showResults = true
          this.fetchHistory() // 刷新历史记录

          ElMessage.success(`蒸馏完成！消耗${data.stats.pointsUsed}点，剩余${data.stats.remainingPoints}点`)
        } else {
          ElMessage.error(data.error || '蒸馏失败')
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '蒸馏失败'
        ElMessage.error(`蒸馏失败: ${errorMsg}`)
      } finally {
        this.distilling = false
      }
    },
    // 添加点数格式化方法
    formatPoints(points) {
      // 修复浮点数显示问题
      const num = parseFloat(points);
      if (isNaN(num)) return '0.00';

      // 保留2位小数，去除多余的0
      return parseFloat(num.toFixed(2)).toString();
    },
    // 更新结果数据
    updateResultsData(data) {
      switch (this.activeMainButton) {
        case 'search':
          this.searchKeywords = data.map(text => ({
            text: text,
            heat: this.getRandomHeat(),
            competition: this.getRandomCompetition()
          }))
          break
        case 'qa':
          this.qaResults = data.map(item => ({
            question: item.question,
            answer: item.answer
          }))
          break
        case 'brand':
          this.brandResults = data.map(item => ({
            name: item.name,
            type: item.type || '相关',
            description: item.description || '',
            searchVolume: this.formatSearchVolume(),
            relatedCount: Math.floor(Math.random() * 20) + 5
          }))
          break
      }
    },

    // 获取历史记录
    async fetchHistory() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/site/api/distillation_management.php`,
          {
            action: 'history',
            store_id: this.userInfo.id,
            page_size: 10
          }
        )

        if (data.data) {
          this.historyRecords = data.data.map(record => ({
            id: record.id,
            keyword: record.keyword,
            type: record.distillation_type,
            time: this.formatTime(record.create_time),
            resultCount: record.result_count,
            pointsDeducted: record.points_deducted,
            distilled_keywords: record.distilled_keywords || [],
            user_id: record.user_id,
            // 预处理用户昵称
            user_nickname: this.getUserNickname(record.user_id)
          }))
        }
      } catch (error) {
        console.error('获取历史记录失败:', error)
      }
    },

    // 切换历史记录详情
    toggleHistoryDetail(recordId) {
      const index = this.expandedHistory.indexOf(recordId)
      if (index > -1) {
        this.expandedHistory.splice(index, 1)
      } else {
        this.expandedHistory.push(recordId)
      }
    },

    // 重新蒸馏
    handleRedistill(record) {
      this.activeMainButton = record.type
      this.searchKeyword = record.keyword
      this.handleDistill()
    },

    // 清空历史记录
    async clearHistory() {
      try {
        await ElMessageBox.confirm(
          '确定要清空所有历史记录吗？',
          '清空确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const { data } = await axios.post(
          `${config.BASE_URL}/site/api/distillation_management.php`,
          {
            action: 'clear_history',
            store_id: this.userInfo.id
          }
        )

        if (data.success) {
          this.historyRecords = []
          this.expandedHistory = []
          ElMessage.success('历史记录已清空')
        } else {
          ElMessage.error(data.error || '清空失败')
        }
      } catch (error) {
        // 用户取消操作
      }
    },

    // 一键复制当前结果
    handleBatchCopy() {
      let text = ''
      switch (this.activeMainButton) {
        case 'search':
          text = this.searchKeywords.map(k => k.text).join('\n')
          break
        case 'qa':
          text = this.qaResults.map(qa => `${qa.question}}`).join('\n')
          break
        case 'brand':
          text = this.brandResults.map(brand => brand.name).join('\n')
          break
      }

      this.handleCopy(text)
      ElMessage.success('已复制全部内容到剪贴板')
    },

    // 复制历史记录全部结果
    handleBatchCopyHistory(record) {
      let text = ''
      switch (record.type) {
        case 'search':
          text = record.distilled_keywords.join('\n')
          break
        case 'qa':
          text = record.distilled_keywords.map(qa => `${qa.question}`).join('\n')
          break
        case 'brand':
          text = record.distilled_keywords.map(brand => brand.name).join('\n')
          break
      }

      this.handleCopy(text)
      ElMessage.success('已复制历史记录全部内容到剪贴板')
    },

    // 复制文本
    handleCopy(text) {
      navigator.clipboard.writeText(text).then(() => {
        ElMessage.success('复制成功')
      }).catch(() => {
        // 降级方案
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        ElMessage.success('复制成功')
      })
    },

    // 工具方法
    getRandomHeat() {
      const heats = ['高', '中', '低']
      return heats[Math.floor(Math.random() * heats.length)]
    },

    getRandomCompetition() {
      const competitions = ['高', '中', '低']
      return competitions[Math.floor(Math.random() * competitions.length)]
    },

    formatSearchVolume() {
      const volume = Math.floor(Math.random() * 50000) + 1000
      return volume >= 10000 ? (volume / 10000).toFixed(1) + '万' : volume.toString()
    },

    formatTime(timeStr) {
      const time = new Date(timeStr)
      const now = new Date()
      const diff = now - time

      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
      if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前'
      if (diff < 2592000000) return Math.floor(diff / 86400000) + '天前'

      return time.getMonth() + 1 + '月' + time.getDate() + '日'
    },

    getTypeLabel(type) {
      const typeMap = {
        search: '搜索词',
        qa: '问答词',
        brand: '品牌词'
      }
      return typeMap[type] || type
    }
  }
}
</script>

<style scoped lang="scss">
.ai-distillation-container {
  padding: 24px;
  min-height: calc(100vh - 160px);
  background: transparent;
}

.main-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
}

.button-group {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.main-button {
  padding: 12px 24px;
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #e0e6ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff7048;
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  &.active {
    background: #ff7048;
    border-color: #ff7048;
  }
}

.content-area {
  min-height: 500px;
}

.distillation-area {
  .search-section {
    margin-bottom: 24px;
  }

  .search-input-group {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;

    .keyword-input {
      flex: 1;
      min-width: 300px;
    }

    .industry-select {
      width: 120px;
    }

    .distill-button {
      background: #ff7048;
      border-color: #ff7048;
      padding: 12px 24px;

      &:hover {
        background: #e55a3a;
        border-color: #e55a3a;
      }
    }
  }
}

.result-section {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;

  .result-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(255, 112, 72, 0.3);

    .result-title {
      color: #e0e6ff;
      font-size: 18px;
      font-weight: 600;
      margin: 0;
    }

    .result-stats {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;

      .stat-item {
        color: #ff7048;
        font-size: 14px;
        font-weight: 500;
      }
    }

    .batch-actions {
      margin-top: 8px;
    }
  }
}

// 卡片头部样式
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  .copy-icon {
    color: #00f2fe;
    padding: 4px;
    margin-left: 8px;
    flex-shrink: 0;

    &:hover {
      color: #ff7048;
      background: rgba(255, 112, 72, 0.1);
    }
  }
}

// 关键词网格样式
.keywords-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
}

.keyword-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  .keyword-text {
    color: #e0e6ff;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    flex: 1;
  }

  .keyword-meta {
    display: flex;
    gap: 12px;

    .meta-item {
      color: #a0a8b8;
      font-size: 12px;
    }
  }
}

// 问答列表样式
.qa-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qa-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff7048;
  }

  .qa-question {
    color: #e0e6ff;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    line-height: 1.5;
    flex: 1;

    .qa-icon {
      color: #ff7048;
      margin-top: 2px;
      flex-shrink: 0;
    }
  }

  .qa-answer {
    color: #a0a8b8;
    font-size: 14px;
    margin-bottom: 16px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    line-height: 1.6;

    .qa-icon {
      color: #00f2fe;
      margin-top: 2px;
      flex-shrink: 0;
    }
  }

  .qa-actions {
    display: flex;
    gap: 8px;
  }
}

// 品牌列表样式
.brand-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.brand-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  .brand-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1;

    .brand-name {
      color: #e0e6ff;
      font-size: 16px;
      font-weight: 600;
    }
  }

  .brand-desc {
    color: #a0a8b8;
    font-size: 14px;
    margin-bottom: 12px;
    line-height: 1.5;
  }

  .brand-stats {
    display: flex;
    gap: 16px;

    .stat {
      color: #ff7048;
      font-size: 12px;
    }
  }
}

// 历史记录样式
.history-section {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 8px;
  padding: 20px;

  .history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .history-title {
      color: #e0e6ff;
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }
  }

  .history-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .history-item {
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    overflow: hidden;

    .history-main {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      background: rgba(255, 255, 255, 0.05);
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.08);
      }

      .history-content {
        display: flex;
        gap: 16px;
        align-items: center;
        flex-wrap: wrap;

        .history-keyword {
          color: #e0e6ff;
          font-weight: 500;
        }

        .history-user {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #00f2fe;
          font-size: 12px;
          padding: 4px 8px;
          background: rgba(0, 242, 254, 0.15);
          border: 1px solid rgba(0, 242, 254, 0.3);
          border-radius: 12px;
          font-weight: 500;

          .user-icon {
            font-size: 12px;
          }
        }

        .history-type {
          color: #ff7048;
          font-size: 12px;
          padding: 2px 8px;
          background: rgba(255, 112, 72, 0.2);
          border-radius: 4px;
        }

        .history-count {
          color: #00f2fe;
          font-size: 12px;
          background: rgba(0, 242, 254, 0.2);
          padding: 2px 6px;
          border-radius: 4px;
        }

        .history-points {
          color: #ff7048;
          font-size: 12px;
          background: rgba(255, 112, 72, 0.2);
          padding: 2px 6px;
          border-radius: 4px;
        }

        .history-time {
          color: #a0a8b8;
          font-size: 12px;
        }
      }

      .expand-icon {
        color: #a0a8b8;
        transition: transform 0.3s ease;

        &.rotated {
          transform: rotate(180deg);
        }
      }
    }

    .history-detail {
      background: rgba(0, 0, 0, 0.3);
      border-top: 1px solid rgba(255, 255, 255, 0.1);

      .detail-content {
        padding: 20px;
        max-height: 400px;
        overflow-y: auto;

        .detail-grid,
        .detail-list {
          margin-top: 0;
        }

        .detail-card {
          margin-bottom: 12px;
          transform: none;

          &:hover {
            transform: none;
            border-color: rgba(255, 255, 255, 0.2);
          }
        }
      }

      .detail-actions {
        padding: 12px 20px;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        gap: 12px;
        justify-content: flex-end;
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .ai-distillation-container {
    padding: 16px;
  }

  .button-group {
    gap: 12px;

    .main-button {
      padding: 10px 20px;
      font-size: 13px;
    }
  }

  .search-input-group {
    flex-direction: column;
    align-items: stretch;

    .keyword-input,
    .industry-select {
      width: 100% !important;
      min-width: auto !important;
    }
  }

  .result-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start !important;
  }

  .result-stats {
    flex-direction: column;
    gap: 8px !important;
  }

  .keywords-grid,
  .brand-list {
    grid-template-columns: 1fr;
  }

  .history-content {
    flex-direction: column;
    gap: 8px !important;
    align-items: flex-start !important;
  }

  .card-header {
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 16px;
  }

  .button-group {
    justify-content: center;
  }
}
</style>

<style>
/* 全局样式 - 修复输入框和选择器问题 */
.distillation-area .el-input__inner,
.distillation-area .el-select .el-input__inner {
  color: #e0e6ff !important;
}

.distillation-area .el-input__inner::placeholder,
.distillation-area .el-select .el-input__inner::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

.distillation-area .el-input__wrapper:hover,
.distillation-area .el-select .el-input__wrapper:hover {
  border-color: #ff7048 !important;
}

.distillation-area .el-input__wrapper.is-focus,
.distillation-area .el-select .el-input__wrapper.is-focus {
  border-color: #ff7048 !important;
  box-shadow: 0 0 0 1px #ff7048 !important;
}

/* 选择器图标颜色 */
.distillation-area .el-select .el-select__caret {
  color: #ff7048 !important;
}

/* 按钮样式统一 */
.distillation-area .el-button {
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.distillation-area .el-button--primary {
  background: #ff7048 !important;
  border-color: #ff7048 !important;
}

.distillation-area .el-button--primary:hover {
  background: #e55a3a !important;
  border-color: #e55a3a !important;
}

.distillation-area .el-button--success {
  background: #00f2fe !important;
  border-color: #00f2fe !important;
}

.distillation-area .el-button--success:hover {
  background: #00c2cc !important;
  border-color: #00c2cc !important;
}
</style>